import {useState, useEffect, useCallback, useMemo} from 'react'
import {Heading} from '@/components/tailwind/heading'
import {Text} from '@/components/tailwind/text'
import {Button} from '@/components/tailwind/button'
import {Field, Label} from '@/components/tailwind/fieldset'
import {Select} from '@/components/tailwind/select'
import {Input} from '@/components/tailwind/input'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation} from '@/i18n/hooks'
import {useToast} from '@/hooks/use-toast'
import {
    downloadPointsPerMemberExport,
    downloadContractorsClientsExport,
    downloadMemberDeploymentsExport,
    downloadServiceListsExport,
    getDataRange,
    StatisticsDataRange,
} from '@/services/statistics-services'
import {TimePeriodSelector, TimePeriod} from '@/components/date-range/time-period-selector'
import {ServiceGroupSelector} from './components/service-group-selector'
import log from '@/lib/logger'
import {ArrowDownTrayIcon} from '@heroicons/react/16/solid'

interface StatisticsCardProps {
    title: string;
    description: string;
    onExport?: () => void;
    isLoading?: boolean;
    disabled?: boolean;
    comingSoonLabel?: string;
    children?: React.ReactNode;
}

function StatisticsCard({
    title,
    description,
    onExport,
    isLoading = false,
    disabled = false,
    comingSoonLabel,
    children,
}: StatisticsCardProps) {
    const {t} = useReportsTranslation()

    return (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-6 shadow-sm">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                        {title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                        {description}
                    </p>
                </div>
            </div>
            {children && <div className="mt-4">{children}</div>}
            <div className="mt-4">
                {disabled ? (
                    <span className="inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-800 px-3 py-1.5 text-sm text-zinc-500 dark:text-zinc-400">
                        {comingSoonLabel}
                    </span>
                ) : (
                    <Button
                        color="blue"
                        onClick={onExport}
                        disabled={isLoading}
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        {isLoading ? t('statistics.export.loading') : t('statistics.export.button')}
                    </Button>
                )}
            </div>
        </div>
    )
}

function StatisticsPage() {
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {toast} = useToast()
    const [loadingExport, setLoadingExport] = useState<string | null>(null)
    const [dataRange, setDataRange] = useState<StatisticsDataRange | null>(null)
    const [anAgPeriod, setAnAgPeriod] = useState<TimePeriod | null>(null)
    const [memberDeploymentsPeriod, setMemberDeploymentsPeriod] = useState<TimePeriod | null>(null)
    const [selectedServiceGroupIds, setSelectedServiceGroupIds] = useState<string[] | null>(null)
    const [pointsSnapshotMode, setPointsSnapshotMode] = useState<'now' | 'custom'>('now')
    const [pointsSnapshotDate, setPointsSnapshotDate] = useState<string>('')

    useEffect(() => {
        async function fetchDataRange() {
            if (!token) return
            try {
                const range = await getDataRange(token)
                setDataRange(range)
            } catch (err) {
                log.error('Error fetching data range:', err)
            }
        }
        fetchDataRange()
    }, [token])

    // Use earliestOrderDate to bound the time period selection: the earliest order marks the start of the
    // period over which activity-based statistics can exist. Fall back to earliestDate if no orders exist yet.
    const effectiveEarliestDate = useMemo(() => {
        if (!dataRange) return null
        return dataRange.earliestOrderDate ?? dataRange.earliestDate
    }, [dataRange])

    const handleAnAgPeriodChange = useCallback((period: TimePeriod | null) => {
        setAnAgPeriod(period)
    }, [])

    const handleMemberDeploymentsPeriodChange = useCallback((period: TimePeriod | null) => {
        setMemberDeploymentsPeriod(period)
    }, [])

    const handleServiceGroupsChange = useCallback((selectedIds: string[] | null) => {
        setSelectedServiceGroupIds(selectedIds)
    }, [])

    async function handleExportPointsPerMember() {
        if (!token) return
        if (pointsSnapshotMode === 'custom' && !pointsSnapshotDate) return

        setLoadingExport('pointsPerMember')
        try {
            const asOfDate = pointsSnapshotMode === 'custom' ? pointsSnapshotDate : undefined
            await downloadPointsPerMemberExport(token, asOfDate)
        } catch (err) {
            log.error('Error exporting points per member:', err)
            toast({
                variant: 'destructive',
                title: t('statistics.export.error'),
                description: t('statistics.export.errorDescription'),
            })
        } finally {
            setLoadingExport(null)
        }
    }

    async function handleExportContractorsClients() {
        if (!token || !anAgPeriod) return

        setLoadingExport('contractorsClients')
        try {
            await downloadContractorsClientsExport(token, anAgPeriod.startDate, anAgPeriod.endDate)
        } catch (err) {
            log.error('Error exporting contractors/clients:', err)
            toast({
                variant: 'destructive',
                title: t('statistics.export.error'),
                description: t('statistics.export.errorDescription'),
            })
        } finally {
            setLoadingExport(null)
        }
    }

    async function handleExportMemberDeployments() {
        if (!token || !memberDeploymentsPeriod) return

        setLoadingExport('memberDeployments')
        try {
            await downloadMemberDeploymentsExport(token, memberDeploymentsPeriod.startDate, memberDeploymentsPeriod.endDate)
        } catch (err) {
            log.error('Error exporting member deployments:', err)
            toast({
                variant: 'destructive',
                title: t('statistics.export.error'),
                description: t('statistics.export.errorDescription'),
            })
        } finally {
            setLoadingExport(null)
        }
    }

    async function handleExportServiceLists() {
        if (!token) return

        setLoadingExport('serviceLists')
        try {
            await downloadServiceListsExport(token, selectedServiceGroupIds)
        } catch (err) {
            log.error('Error exporting service lists:', err)
            toast({
                variant: 'destructive',
                title: t('statistics.export.error'),
                description: t('statistics.export.errorDescription'),
            })
        } finally {
            setLoadingExport(null)
        }
    }

    return (
        <div className="max-w-6xl mx-auto">
            <div className="mb-8">
                <Heading level={1}>{t('statistics.title')}</Heading>
                <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
                    {t('statistics.description')}
                </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <StatisticsCard
                    title={t('statistics.cards.pointsPerMember.title')}
                    description={t('statistics.cards.pointsPerMember.description')}
                    onExport={handleExportPointsPerMember}
                    isLoading={loadingExport === 'pointsPerMember'}
                >
                    <div className="space-y-3">
                        <Field>
                            <Label>{t('statistics.cards.pointsPerMember.snapshotDate')}</Label>
                            <Select
                                value={pointsSnapshotMode}
                                onChange={(e) => setPointsSnapshotMode(e.target.value as 'now' | 'custom')}
                            >
                                <option value="now">
                                    {t('statistics.cards.pointsPerMember.snapshotNow')}
                                </option>
                                <option value="custom">
                                    {t('statistics.cards.pointsPerMember.snapshotCustom')}
                                </option>
                            </Select>
                        </Field>
                        {pointsSnapshotMode === 'custom' && (
                            <Field>
                                <Label>{t('statistics.cards.pointsPerMember.snapshotDate')}</Label>
                                <Input
                                    type="date"
                                    value={pointsSnapshotDate}
                                    onChange={(e) => setPointsSnapshotDate(e.target.value)}
                                    max={new Date().toISOString().slice(0, 10)}
                                />
                            </Field>
                        )}
                    </div>
                </StatisticsCard>

                <StatisticsCard
                    title={t('statistics.cards.contractorsClients.title')}
                    description={t('statistics.cards.contractorsClients.description')}
                    onExport={handleExportContractorsClients}
                    isLoading={loadingExport === 'contractorsClients'}
                >
                    <TimePeriodSelector
                        earliestDate={effectiveEarliestDate}
                        latestDate={dataRange?.latestDate}
                        onChange={handleAnAgPeriodChange}
                    />
                </StatisticsCard>

                <StatisticsCard
                    title={t('statistics.cards.memberDeployments.title')}
                    description={t('statistics.cards.memberDeployments.description')}
                    onExport={handleExportMemberDeployments}
                    isLoading={loadingExport === 'memberDeployments'}
                >
                    <TimePeriodSelector
                        earliestDate={effectiveEarliestDate}
                        latestDate={dataRange?.latestDate}
                        onChange={handleMemberDeploymentsPeriodChange}
                    />
                </StatisticsCard>

                <StatisticsCard
                    title={t('statistics.cards.serviceLists.title')}
                    description={t('statistics.cards.serviceLists.description')}
                    onExport={handleExportServiceLists}
                    isLoading={loadingExport === 'serviceLists'}
                >
                    <ServiceGroupSelector
                        onChange={handleServiceGroupsChange}
                    />
                </StatisticsCard>
            </div>
        </div>
    )
}

export default StatisticsPage
